import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { compradorTrpc } from '../../lib/compradorTrpc';
import { AppShellComprador } from '../../components/comprador/AppShellComprador';
import { useCompradorSessao, rotaApp } from './util';

/**
 * "Criar senha" (opcional) DENTRO do app do comprador. Quem quiser entrar mais
 * rápido cria uma senha; quem não quiser segue só no link mágico + sessão
 * longa. Nunca é exigida na entrada. Mínimo 8 caracteres.
 */
export function SenhaComprador() {
  const slug = useCompradorSessao();
  const [org, setOrg] = useState<{ orgNome: string; corMarca: string | null } | null>(null);
  const [jaTem, setJaTem] = useState<boolean | null>(null);
  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (!slug) return;
    compradorTrpc.auth.me.query({ orgSlug: slug }).then((m) => setOrg({ orgNome: m.orgNome, corMarca: m.corMarca })).catch(() => {});
    compradorTrpc.auth.temSenha.query({ orgSlug: slug }).then((r) => setJaTem(r.temSenha)).catch(() => {});
  }, [slug]);

  async function salvar(): Promise<void> {
    if (!slug) return;
    setErro(null);
    if (senha.length < 8) return setErro('A senha precisa ter pelo menos 8 caracteres.');
    if (senha !== confirma) return setErro('As duas senhas não são iguais.');
    setSalvando(true);
    try {
      await compradorTrpc.auth.definirSenha.mutate({ orgSlug: slug, senha });
      setOk(true);
      setJaTem(true);
      setSenha('');
      setConfirma('');
    } catch (e) {
      setErro((e as Error).message || 'Não foi possível salvar a senha.');
    } finally {
      setSalvando(false);
    }
  }

  const input = 'h-[46px] w-full rounded-xl border border-line-2 bg-surface px-[15px] text-sm text-ink outline-none focus:border-accent';

  return (
    <AppShellComprador corMarca={org?.corMarca} orgNome={org?.orgNome}>
      <div className="mx-auto max-w-md">
        <h1 className="font-serif text-[26px] font-medium text-ink">
          {jaTem ? 'Trocar sua senha' : 'Criar uma senha'}
        </h1>
        <p className="mt-1.5 text-sm text-ink-3">
          Opcional. Com uma senha você entra mais rápido, sem esperar o link no e-mail. O link continua funcionando sempre.
        </p>

        {ok ? (
          <div className="mt-6 rounded-2xl border border-line bg-white p-5" data-testid="senha-ok">
            <div className="font-serif text-lg text-ink">Senha salva</div>
            <p className="mt-1 text-sm text-ink-3">Da próxima vez, é só escolher &ldquo;Já tenho senha&rdquo; na tela de entrada.</p>
            <Link href={rotaApp(slug ?? '')} className="mt-4 inline-block text-sm font-medium text-accent">
              Voltar para o Início
            </Link>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void salvar();
            }}
            className="mt-6 space-y-4"
          >
            <div>
              <label className="mb-2 block text-[12.5px] font-medium text-ink-2" htmlFor="nova-senha">
                Nova senha
              </label>
              <input
                id="nova-senha"
                type="password"
                autoComplete="new-password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                placeholder="pelo menos 8 caracteres"
                className={input}
                data-testid="nova-senha-input"
              />
            </div>
            <div>
              <label className="mb-2 block text-[12.5px] font-medium text-ink-2" htmlFor="confirma-senha">
                Repita a senha
              </label>
              <input
                id="confirma-senha"
                type="password"
                autoComplete="new-password"
                value={confirma}
                onChange={(e) => setConfirma(e.target.value)}
                placeholder="repita a senha"
                className={input}
                data-testid="confirma-senha-input"
              />
            </div>
            {erro && (
              <div
                className="rounded-xl px-3 py-2.5 text-xs"
                style={{ background: 'var(--risk-soft)', border: '1px solid var(--risk-line)', color: 'var(--risk)' }}
              >
                {erro}
              </div>
            )}
            <button
              type="submit"
              disabled={salvando}
              data-testid="salvar-senha"
              className="w-full rounded-2xl py-3.5 text-[15px] font-semibold text-white disabled:opacity-60"
              style={{ background: 'var(--brand, var(--accent))' }}
            >
              {salvando ? 'Salvando…' : 'Salvar senha'}
            </button>
            <div className="text-center">
              <Link href={rotaApp(slug ?? '')} className="text-[13px] text-ink-3 hover:text-ink">
                Agora não
              </Link>
            </div>
          </form>
        )}
      </div>
    </AppShellComprador>
  );
}
